import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StoreService } from '../../store.service';
import { DataService } from '../../data.service';
import { DealerService } from '../../dealer.service';
import { AI_SUMMARY_MIN_DURATION_MS, AI_SUMMARY_QUERY_PARAM } from 'src/app/core/session-replay-ai';

interface SessionItem {
  session_id: string;
  page?: string;
  page_url?: string;
  started_at?: string;
  ended_at?: string;
  updated_at?: string;
  duration_ms?: number;
  duration?: number;
  [key: string]: any;
}

@Component({
  selector: 'app-replay-sessions',
  templateUrl: './replay-sessions.component.html',
  styleUrls: ['./replay-sessions.component.scss'],
})
export class ReplaySessionsComponent implements OnInit {
  bearer: string;
  storeId: string;
  visitorId: string;
  sessions: SessionItem[] = [];
  isLoading = true;
  sessionReplayEnabled = true;

  currentPage = 1;
  pageSize = 20;
  totalRecord = 0;
  totalPage = 0;
  start = 0;
  end = 0;
  returnVp: number | null = null;
  private scrollToSessionId: string | null = null;

  // Search & client-side filtering
  searchQuery = '';
  activeDurationFilter = 'all'; // 'all' | 'active' | 'bounced' | '30s' | '60s'
  showInfoBanner = true;
  copiedId: string | null = null;

  DATE_FILTERS = [
    { display: 'Today', value: 'today' },
    { display: 'Yesterday', value: 'yesterday' },
    { display: 'Last 14 Days', value: 'last14' },
  ];
  selectedDateFilter = this.DATE_FILTERS[2];
  startDate: string;
  endDate: string;

  PAGE_FILTERS = [
    { display: 'All Pages', value: '' },
    { display: 'Homepage', value: 'Homepage' },
    { display: 'Custom Page', value: 'Custom Page' },
    { display: 'Landing Page', value: 'Landing Page' },
    { display: 'Checkout Page', value: 'Checkout Page' },
    { display: 'Product Page', value: 'Product Page' },
  ];
  selectedPageFilter = this.PAGE_FILTERS[0];

  DURATION_FILTERS = [
    { label: 'All', value: 'all' },
    { label: 'Active (> 5s)', value: 'active' },
    { label: '> 30s', value: '30s' },
    { label: '> 1 min', value: '60s' },
  ];

  constructor(
    private svc: StoreService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private dataService: DataService,
    private dealerService: DealerService,
  ) {}

  ngOnInit(): void {
    this.bearer = localStorage.getItem('bearer');
    this.storeId = localStorage.getItem('store_id');
    this.visitorId = this.route.snapshot.paramMap.get('visitorId');

    const pageParam = Number(this.route.snapshot.queryParamMap.get('page'));
    if (pageParam > 0) this.currentPage = pageParam;
    this.scrollToSessionId = this.route.snapshot.queryParamMap.get('session');
    const vpParam = Number(this.route.snapshot.queryParamMap.get('vp'));
    this.returnVp = vpParam > 0 ? vpParam : null;

    if (localStorage.getItem('dismiss_replay_notice') === 'true') {
      this.showInfoBanner = false;
    }

    this.applyDateFilter();
    this.checkSessionReplaySetting();
    this.loadSessions();
  }

  checkSessionReplaySetting(): void {
    const storeId = this.storeId || localStorage.getItem('store_id');
    if (!storeId) {
      this.sessionReplayEnabled = true;
      return;
    }
    this.svc.getStoreConfig(this.bearer, storeId, 'session_replay').subscribe(
      (res: any) => {
        const data = res?.data;
        if (!data || data?.error === null) {
          this.sessionReplayEnabled = true;
          return;
        }
        const rawVal = data?.value !== undefined ? data.value : (data?.session_replay !== undefined ? data.session_replay : data);
        if (rawVal === undefined || rawVal === null) {
          this.sessionReplayEnabled = true;
          return;
        }
        const strVal = String(rawVal).trim().toLowerCase();
        this.sessionReplayEnabled = strVal !== 'false' && strVal !== '0' && strVal !== 'disabled' && strVal !== 'off';
      },
      () => { this.sessionReplayEnabled = true; }
    );
  }

  applyDateFilter(): void {
    const today = moment();
    if (this.selectedDateFilter.value === 'today') {
      this.startDate = today.format('YYYY-MM-DD');
      this.endDate   = today.format('YYYY-MM-DD');
    } else if (this.selectedDateFilter.value === 'yesterday') {
      const yesterday = today.clone().subtract(1, 'days');
      this.startDate = yesterday.format('YYYY-MM-DD');
      this.endDate   = yesterday.format('YYYY-MM-DD');
    } else {
      this.startDate = today.clone().subtract(13, 'days').format('YYYY-MM-DD');
      this.endDate   = today.format('YYYY-MM-DD');
    }
  }

  selectDateFilter(filter: { display: string; value: string }): void {
    if (this.selectedDateFilter.value === filter.value) return;
    this.selectedDateFilter = filter;
    this.currentPage = 1;
    this.applyDateFilter();
    this.loadSessions();
  }

  selectPageFilter(filter: { display: string; value: string }): void {
    if (this.selectedPageFilter.value === filter.value) return;
    this.selectedPageFilter = filter;
    this.currentPage = 1;
    this.loadSessions();
  }

  setDurationFilter(val: string): void {
    this.activeDurationFilter = val;
  }

  dismissNotice(): void {
    this.showInfoBanner = false;
    localStorage.setItem('dismiss_replay_notice', 'true');
  }

  loadSessions(): void {
    this.isLoading = true;
    this.svc.getReplaySessions(
      this.bearer, this.storeId, this.visitorId,
      this.currentPage, this.pageSize,
      this.startDate, this.endDate,
      this.selectedPageFilter.value || undefined,
    ).subscribe(
      (res) => {
        const rawList: SessionItem[] = res?.data?.data ?? [];
        // Deduplicate sessions with identical session_id (retaining the latest timestamp)
        const sessionMap = new Map<string, SessionItem>();
        rawList.forEach((s) => {
          if (!s.session_id) return;
          const existing = sessionMap.get(s.session_id);
          if (!existing) {
            sessionMap.set(s.session_id, s);
          } else {
            const existingTime = new Date(existing.started_at || 0).getTime();
            const newTime = new Date(s.started_at || 0).getTime();
            if (newTime >= existingTime) {
              sessionMap.set(s.session_id, s);
            }
          }
        });
        this.sessions = Array.from(sessionMap.values());
        const pagination = res?.data?.pagination;
        if (pagination) {
          this.currentPage = pagination.page;
          this.totalRecord = pagination.total_record;
          this.totalPage = pagination.total_page;
        }
        this.start = (this.currentPage - 1) * this.pageSize;
        this.end = this.start + this.sessions.length;
        this.isLoading = false;

        if (this.scrollToSessionId) {
          const targetId = this.scrollToSessionId;
          this.scrollToSessionId = null;
          setTimeout(() => {
            const el = document.getElementById('session-' + targetId);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  getSessionDuration(s: SessionItem): number {
    try {
      const cached = sessionStorage.getItem('replay_dur_' + s.session_id);
      if (cached && Number(cached) > 0) {
        return Number(cached);
      }
    } catch {}

    if (typeof s.duration_ms === 'number' && s.duration_ms > 0) {
      return s.duration_ms;
    }
    if (typeof s.duration === 'number' && s.duration > 0) {
      return s.duration > 1000 ? s.duration : s.duration * 1000;
    }
    if (s.ended_at && s.started_at) {
      const diff = new Date(s.ended_at).getTime() - new Date(s.started_at).getTime();
      if (diff > 0) return diff;
    }
    if (s.updated_at && s.started_at) {
      const diff = new Date(s.updated_at).getTime() - new Date(s.started_at).getTime();
      if (diff > 0) return diff;
    }
    return s.duration_ms || 0;
  }

  isBounce(s: SessionItem): boolean {
    return this.getSessionDuration(s) <= 0;
  }

  get filteredSessions(): SessionItem[] {
    if (!this.sessions) return [];
    let list = this.sessions;

    if (this.activeDurationFilter === 'active') {
      list = list.filter(s => this.getSessionDuration(s) >= 5000);
    } else if (this.activeDurationFilter === 'bounced') {
      list = list.filter(s => this.getSessionDuration(s) <= 0);
    } else if (this.activeDurationFilter === '30s') {
      list = list.filter(s => this.getSessionDuration(s) >= 30000);
    } else if (this.activeDurationFilter === '60s') {
      list = list.filter(s => this.getSessionDuration(s) >= 60000);
    }

    if (this.searchQuery && this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      list = list.filter(s =>
        (s.session_id && s.session_id.toLowerCase().includes(q)) ||
        (s.page && s.page.toLowerCase().includes(q)) ||
        (s.page_url && s.page_url.toLowerCase().includes(q))
      );
    }

    return list;
  }

  get displayedMetrics() {
    const list = this.filteredSessions;
    if (!list || list.length === 0) {
      return {
        totalSessions: 0,
        avgDurationMs: 0,
        totalDurationMs: 0,
        topPage: '-',
      };
    }

    let totalDur = 0;
    let counted = 0;
    const pageCounts: { [key: string]: number } = {};

    list.forEach(s => {
      const dur = this.getSessionDuration(s);
      totalDur += dur;
      if (dur > 0) counted++;
      const p = s.page || 'Unknown';
      pageCounts[p] = (pageCounts[p] || 0) + 1;
    });

    let topPage = '-';
    let maxCount = 0;
    Object.keys(pageCounts).forEach(k => {
      if (pageCounts[k] > maxCount) {
        maxCount = pageCounts[k];
        topPage = k;
      }
    });

    return {
      totalSessions: list.length,
      avgDurationMs: counted > 0 ? Math.round(totalDur / counted) : Math.round(totalDur / list.length),
      totalDurationMs: totalDur,
      topPage,
    };
  }

  onPageChange(): void {
    this.loadSessions();
  }

  private async getDealerCredentials(): Promise<{ dealerId: string; dealerUsername: string }> {
    const info = JSON.parse(localStorage.getItem('5s-info') || '{}');
    const directInfo = this.dataService?.getDealerInfo() || this.dealerService?.dealerData;
    let dealerId = info.d_id || info.dealer_id || directInfo?.dealer_id || directInfo?.d_id || '';
    let dealerUsername = info.username || directInfo?.username || localStorage.getItem('username') || localStorage.getItem('dealer_username') || '';

    if (!dealerUsername || !dealerId) {
      try {
        const fetched: any = await this.dealerService.getDealer().toPromise();
        if (fetched) {
          if (!dealerUsername) dealerUsername = fetched.username || '';
          if (!dealerId) dealerId = fetched.dealer_id || fetched.d_id || '';
        }
      } catch (e) {}
    }

    return { dealerId, dealerUsername };
  }

  async trackWatchFail(sessionId: string, errorReason: string): Promise<void> {
    try {
      const { dealerId, dealerUsername } = await this.getDealerCredentials();

      const eventData: any = {
        data: {
          d_id: dealerId,
          type: 'Session Replay Watch Fail',
          distinct_id: dealerId,
          dealer_username: dealerUsername,
          store_id: this.storeId,
          visitor_id: this.visitorId,
          session_id: sessionId,
          platform: 'dashboard',
          error_response: errorReason,
        },
        application: 'dashboard',
        driver: 'mixpanel',
      };

      this.svc.publish_event(eventData).subscribe(
        () => {},
        (err) => console.log('Mixpanel tracking error:', err)
      );
    } catch (e) {
      console.log('Error tracking watch fail event:', e);
    }
  }

  /**
   * Same rule as the player's AI Summary button: recordings under 10 seconds have too little
   * activity to analyse. This uses the list's duration; the player re-checks against the
   * recording it loads, so a session that turns out shorter is never summarised (or charged).
   */
  isAiSummaryDisabled(s: SessionItem): boolean {
    return this.getSessionDuration(s) < AI_SUMMARY_MIN_DURATION_MS;
  }

  aiSummaryTooltip(s: SessionItem): string {
    return this.isAiSummaryDisabled(s)
      ? 'AI summary requires a recording of at least 10 seconds'
      : 'Summarize recording & generate AI insights';
  }

  /** Opens the player and has it start the AI summary once the recording has loaded. */
  openAiSummary(s: SessionItem, event?: Event): void {
    event?.stopPropagation();
    if (this.isAiSummaryDisabled(s)) return;
    this.openPlayer(s, { [AI_SUMMARY_QUERY_PARAM]: 1 });
  }

  openPlayer(sessionOrId: SessionItem | string, extraQueryParams: Record<string, any> = {}): void {
    const sessionId = typeof sessionOrId === 'string' ? sessionOrId : sessionOrId?.session_id;
    const queryParams: any = { page: this.currentPage, ...extraQueryParams };
    if (this.returnVp) queryParams.vp = this.returnVp;
    this.router.navigate(['/dashboard/replay', this.visitorId, sessionId], { queryParams })
      .then((navSuccess) => {
        if (navSuccess === false) {
          this.trackWatchFail(sessionId, 'Navigation cancelled');
        }
      })
      .catch((navError) => {
        this.trackWatchFail(sessionId, navError?.message || 'Navigation error');
      });
  }

  copyText(text: string, id: string, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      this.copiedId = id;
      setTimeout(() => {
        if (this.copiedId === id) this.copiedId = null;
      }, 2000);
    });
  }

  formatDuration(ms: number): string {
    if (!ms || ms <= 0) return '< 1s';
    const s = Math.floor(ms / 1000);
    if (s < 1) return '< 1s';
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    if (h > 0) {
      return `${h}h ${m % 60}m ${s % 60}s`;
    }
    return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
  }

  shortId(id: string): string {
    return id ? id.split('-')[0] : '-';
  }

  cleanUrl(url: string): string {
    if (!url) return '-';
    try {
      const parsed = new URL(url);
      let path = parsed.pathname || '/';
      try {
        path = decodeURIComponent(path);
      } catch {}
      let search = '';
      if (parsed.search) {
        try {
          search = decodeURIComponent(parsed.search);
        } catch {
          search = parsed.search;
        }
      }
      return path + search;
    } catch {
      let u = url.replace(/^https?:\/\/[^/]+/, '') || url;
      try {
        u = decodeURIComponent(u);
      } catch {}
      return u;
    }
  }

  getActivityDots(durationMs: number): number[] {
    const dur = durationMs || 0;
    let score = 1;
    if (dur > 60000) score = 5;
    else if (dur > 30000) score = 4;
    else if (dur > 15000) score = 3;
    else if (dur > 5000) score = 2;
    return Array.from({ length: 5 }, (_, i) => (i < score ? 1 : 0));
  }
}
